import type { FC } from "react";
import { memo } from "react";
import { useSearchParams } from "react-router-dom";

import { FetchRawGet } from "@/lib/fetch-helper";
import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import type { PropsWithStyleX } from "@/styles/tokens.stylex";
import backgd from "@ComponentImages/ContentContainers/background.png";
import { onDefaultKeyboardUp, urlFormat } from "@Components/Modules/Helpers";
import TelemetryHoc from "@Components/telemetry-hoc/telemetry-hoc";
import { Common } from "@Routes/CommonLinks";
import * as stylex from "@stylexjs/stylex";

export interface IContentContainer extends PropsWithStyleX {
    clientId?: string;
    pageTitle?: string;
    parent?: string;
    disabled?: boolean;
    onViewTelemetry?: ITelemetryCustom;
    onClickTelemetry?: ITelemetryCustom;
    data?: string;
}

const renderConfig = ({ parent, pageTitle }: IContentContainer) => (
    <div {...stylex.props(styles.pagePath)}>
        {parent && (
            <div {...stylex.props(styles.configBlock)}>
                <div>Parent</div>
                <div {...stylex.props(styles.value)}>{parent}</div>
            </div>
        )}
        <div {...stylex.props(styles.configBlock)}>
            <div>Title</div>
            <div {...stylex.props(styles.value)}>{pageTitle}</div>
        </div>
        <div {...stylex.props(styles.configBlock)}>
            <div>Preview title</div>
            <div {...stylex.props(styles.value)}>
                {pageTitle}
                -preview
            </div>
        </div>
    </div>
);

const ContentContainer: FC<IContentContainer> = memo((props) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get("cms-preview") === "true";
    const showConfig = searchParams.get("cms-containers") === "true";

    const { data } = useQueryCustomNoSuspense({
        queryKey: [preview, props.pageTitle, props.parent],
        queryFn: () =>
            FetchRawGet(
                urlFormat(
                    Common,
                    {
                        action: "GetContent",
                        preview,
                        pageTitle: props.pageTitle,
                        parent: props.parent,
                    },
                    true,
                ),
            ),
        initialData: props.data,
    });

    if (!showConfig && (!data || props.disabled)) {
        return null;
    }

    return (
        <TelemetryHoc
            id={props.clientId}
            styleX={[styles.contentContainerComponent, showConfig && styles.showPath(backgd), props.styleX]}
            role="banner"
            onClick={() => sendTelemetryEvent(props.onClickTelemetry)}
            onKeyUp={onDefaultKeyboardUp}
            tabIndex={-1}
            telemetryCustom={props.onViewTelemetry}
        >
            {showConfig ? renderConfig(props) : <div dangerouslySetInnerHTML={{ __html: data }} />}
        </TelemetryHoc>
    );
});

const styles = stylex.create({
    contentContainerComponent: {},
    showPath: (img: string) => ({
        borderColor: "#808080",
        borderStyle: "dashed",
        borderWidth: 1,

        color: "#808080",
        fontSize: "0.875rem",
        position: "relative",

        "::before": {
            backgroundImage: `url(${img}) repeat-x center center`,
            backgroundSize: "contain",
            content: "",
            opacity: 0.1,
            position: "absolute",
            height: "100%",
            width: "100%",
        },
    }),

    pagePath: {
        textAlign: "center",
    },
    configBlock: {
        paddingBottom: 5,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 5,
    },

    value: {
        backgroundColor: "#808080",
        color: "#fff",
        display: "inline-block",
    },
});

export default ContentContainer;
